import { type FC, memo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { TriggerButton, TriggerButtonGroup, TriggerButtonSize } from "widgets";

import { conditionallyPluralizeTrackLabel } from "../../../app-utils/string.js";
import { type IPileup } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { type CoordinateList } from "../../../graphql.js";
import { type ISortableTrack } from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import { ModalActionWidget } from "../../core/overlays/index.js";
import { TrackHeader } from "../TrackHeader.js";
import { FeaturesLimitToggleButton } from "../features-track/FeatureTrackHeader.js";
import CoordinatesTrackCustomizationDialog from "@/ui-modules/genome-viewer/coordinate-track/CoordinatesTrackCustomizationDialog.tsx";

export interface ICoordinateTrackHeaderProps {
  track: ISortableTrack;
  pileup: IPileup;
  overflow: boolean;
  showAllFeatures: boolean;
  coordinateListId: number;

  isTrackHovered?: boolean;
}

const CoordinateTrackHeader: FC<ICoordinateTrackHeaderProps> = (props) => {
  const { pileup, showAllFeatures, overflow, track, coordinateListId } = props;

  const coordinateList = useSelector<IAppState, CoordinateList>(
    (state) => state.genomeBrowser.coordinateLists[coordinateListId],
    shallowEqual,
  );

  return (
    <TrackHeader
      title={conditionallyPluralizeTrackLabel(track)}
      subtitle={coordinateList?.name}
      isTrackHovered={props.isTrackHovered}
      trackIcons={
        <TriggerButtonGroup vertical>
          <ModalActionWidget
            actionWidget={({ open }) => (
              <TriggerButton
                icon={"settings"}
                tooltip={`Customize ${conditionallyPluralizeTrackLabel(track)} track`}
                label={"Configure"}
                onPress={() => open()}
                size={TriggerButtonSize.Small}
              />
            )}
          >
            {({ close }) => <CoordinatesTrackCustomizationDialog onClose={close} />}
          </ModalActionWidget>
          <FeaturesLimitToggleButton
            track={track}
            trackSpec={{ ...track, coordinateListId: coordinateListId }}
            pileup={pileup}
            overflow={overflow}
            showAllFeatures={showAllFeatures}
          />
        </TriggerButtonGroup>
      }
    />
  );
};

/// --- Export --- ///
const MemoCoordinateTrackHeader = memo(CoordinateTrackHeader, shallowEqual);

export { MemoCoordinateTrackHeader as CoordinateTrackHeader };
